import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';
import ClassContextPicker, { useSchoolConfig } from '../../../components/common/ClassContextPicker';

// ─── Constants ───────────────────────────────────────────────────────────────
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DEFAULT_PERIOD_TIMES = [
  { period: 1, startTime: '08:30', endTime: '09:15' },
  { period: 2, startTime: '09:15', endTime: '10:00' },
  { period: 3, startTime: '10:00', endTime: '10:45' },
  { period: 4, startTime: '11:00', endTime: '11:45' },
  { period: 5, startTime: '11:45', endTime: '12:30' },
  { period: 6, startTime: '13:15', endTime: '14:00' },
  { period: 7, startTime: '14:00', endTime: '14:45' },
  { period: 8, startTime: '14:45', endTime: '15:30' },
];
const cellKey = (day, period) => `${day}|${period}`;

// ─── Main Component ──────────────────────────────────────────────────────────
export default function StaffTimetablePage() {
  const { user, tenant } = useAuth();
  const role = user?.roleKey || '';
  const canEdit = ['admin','manager','team_lead'].includes(role) || user?.userType === 'super_admin';

  const cfg = useSchoolConfig();

  // Pull every active user once and let the editor filter to "teaching staff"
  // visually — different schools use different role-key conventions
  // (teacher / faculty / staff / principal / Mr. / etc.) so a fixed roleKey
  // filter often returns 0 even when the tenant has staff. Fetching all and
  // sorting client-side is robust to that.
  const [staff, setStaff]         = useState([]);
  const [staffError, setStaffError] = useState(null);
  useEffect(() => {
    // Try with a permissive role filter first — covers most schools.
    const fetchAll = async () => {
      try {
        // Try every plausible roleKey; if none exist, fall back to ALL users.
        const tryParams = [
          { roleKey: 'faculty,teacher,team_lead,principal,staff,vice_principal', limit: 500 },
          { limit: 500 }, // unfiltered fallback
        ];
        for (const params of tryParams) {
          const r = await api.get('/users', { params });
          const list = r.data?.records || r.data?.data || [];
          // Drop students — they're never teaching staff.
          const filtered = list.filter(u => (u.roleKey || '').toLowerCase() !== 'student');
          if (filtered.length > 0) {
            setStaff(filtered);
            return;
          }
        }
        setStaff([]);
      } catch (e) {
        setStaffError(e.response?.data?.message || e.message || 'Failed to load staff list');
        setStaff([]);
      }
    };
    fetchAll();
  }, []);

  const [tab, setTab]                 = useState('grid');     // 'grid' | 'overview' | 'policy'
  const [picked, setPicked]           = useState({ classGrade: '', section: '' });
  const [academicYear, setAcademicYear] = useState('');
  const [periodsPerDay, setPeriodsPerDay] = useState(8);
  const [periodTimes, setPeriodTimes] = useState(DEFAULT_PERIOD_TIMES);
  const [days, setDays]               = useState(DAYS);
  const [schedule, setSchedule]       = useState({});  // map of cellKey -> cell
  const [recordId, setRecordId]       = useState(null);

  const [overview, setOverview]       = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [editCell, setEditCell]       = useState(null);  // { day, period }
  const [cellForm, setCellForm]       = useState({ subject: '', primaryTeacher: '', substituteTeacher: '', petTeacher: '', room: '', notes: '' });
  const [savingCell, setSavingCell]   = useState(false);

  const [savingGrid, setSavingGrid]   = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [toast, setToast]             = useState(null);

  // ─── Fetch grid for current picked class+section ───────────────────────
  const fetchGrid = useCallback(async () => {
    if (!picked.classGrade) {
      setSchedule({}); setRecordId(null); return;
    }
    setLoadingGrid(true);
    try {
      const r = await api.get('/education/timetable/grid', {
        params: { classGrade: picked.classGrade, section: picked.section || '', academicYear },
      });
      const rec = r.data?.data || r.data;
      if (rec) {
        const rd = rec.recordData || {};
        setRecordId(rec._id);
        setPeriodsPerDay(rd.periods_per_day || 8);
        setPeriodTimes(Array.isArray(rd.period_times) && rd.period_times.length ? rd.period_times : DEFAULT_PERIOD_TIMES);
        setDays(Array.isArray(rd.days) && rd.days.length ? rd.days : DAYS);
        const map = {};
        (rd.schedule || []).forEach(s => { map[cellKey(s.day, s.period)] = s; });
        setSchedule(map);
        setAcademicYear(rd.academic_year || academicYear);
      } else {
        setRecordId(null);
        setSchedule({});
      }
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load timetable.' });
    }
    setLoadingGrid(false);
  }, [picked, academicYear]);

  useEffect(() => { if (tab === 'grid') fetchGrid(); }, [tab, fetchGrid]);

  // ─── Fetch overview (every grid) ──────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const r = await api.get('/education/timetable/grids');
      setOverview(r.data?.data?.grids || r.data?.grids || []);
    } catch (_) {}
    setOverviewLoading(false);
  }, []);

  useEffect(() => { if (tab === 'overview') fetchOverview(); }, [tab, fetchOverview]);

  // ─── Save full grid ───────────────────────────────────────────────────
  const saveGrid = async () => {
    if (!picked.classGrade) return setToast({ type: 'danger', msg: 'Pick a class first.' });
    setSavingGrid(true);
    try {
      const scheduleArr = Object.values(schedule);
      await api.put('/education/timetable/grid', {
        classGrade: picked.classGrade,
        section:    picked.section || '',
        academicYear,
        periodsPerDay,
        days,
        period_times: periodTimes.slice(0, periodsPerDay),
        schedule: scheduleArr,
      });
      setToast({ type: 'success', msg: 'Timetable saved.' });
      fetchGrid();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
    setSavingGrid(false);
  };

  // ─── Cell editor ──────────────────────────────────────────────────────
  const openCell = (day, period) => {
    if (!canEdit) return;
    const existing = schedule[cellKey(day, period)] || {};
    setEditCell({ day, period });
    setCellForm({
      subject:           existing.subject || '',
      primaryTeacher:    existing.primaryTeacher || '',
      substituteTeacher: existing.substituteTeacher || '',
      petTeacher:        existing.petTeacher || '',
      room:              existing.room || '',
      notes:             existing.notes || '',
    });
  };
  const closeCell = () => { setEditCell(null); };

  const saveCell = async () => {
    if (!editCell) return;
    const { day, period } = editCell;
    setSavingCell(true);
    try {
      // If grid not yet saved, just update local state. Persist on full save.
      if (!recordId) {
        setSchedule(prev => ({ ...prev, [cellKey(day, period)]: { day, period, ...cellForm } }));
        setToast({ type: 'success', msg: 'Cell updated locally. Click Save Timetable to persist.' });
      } else {
        await api.patch('/education/timetable/grid/cell', {
          classGrade: picked.classGrade, section: picked.section || '',
          day, period, ...cellForm,
        });
        setSchedule(prev => ({ ...prev, [cellKey(day, period)]: { day, period, ...cellForm } }));
        setToast({ type: 'success', msg: 'Cell saved.' });
      }
      closeCell();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Cell save failed.' });
    }
    setSavingCell(false);
  };

  const clearCell = () => {
    if (!editCell) return;
    const { day, period } = editCell;
    setSchedule(prev => {
      const next = { ...prev };
      delete next[cellKey(day, period)];
      return next;
    });
    setToast({ type: 'success', msg: 'Cell cleared. Click Save Timetable to persist.' });
    closeCell();
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="Staff Timetable" icon="bi-calendar3-week"
        subtitle="Class-teacher / substitute / PET assignments per period · per class & section"
        actions={(
          canEdit && tab === 'grid' && picked.classGrade && (
            <button className="btn btn-success btn-sm" onClick={saveGrid} disabled={savingGrid}>
              {savingGrid ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-save me-1"/>}
              Save Timetable
            </button>
          )
        )}/>
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === 'grid' ? 'active' : ''}`} onClick={() => setTab('grid')}>
          <i className="bi bi-grid-3x3-gap me-1"/>Grid
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          <i className="bi bi-list me-1"/>All Class Timetables
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {/* ─────────────────────────────────────────────────── GRID TAB ──── */}
      {tab === 'grid' && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <ClassContextPicker
                value={picked} onChange={setPicked}
                fields={['classGrade','section']} required={['classGrade']}
              />
              <div className="row g-2 mt-2">
                <div className="col-md-3 col-sm-6">
                  <label className="form-label small mb-1">Academic Year</label>
                  <input className="form-control form-control-sm" placeholder="2026-27" value={academicYear} onChange={e => setAcademicYear(e.target.value)} />
                </div>
                <div className="col-md-2 col-sm-6">
                  <label className="form-label small mb-1">Periods / Day</label>
                  <input type="number" min="1" max="12" className="form-control form-control-sm"
                    value={periodsPerDay} onChange={e => setPeriodsPerDay(parseInt(e.target.value) || 8)} />
                </div>
              </div>
            </div>
          </div>

          {/* Header strip */}
          {picked.classGrade && (
            <div className="card mb-3" style={{ background: 'linear-gradient(135deg, #ede9fe, #fce7f3)', border: 'none' }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
                      <i className="bi bi-building me-2 text-primary"/>
                      {tenant?.name || 'School'} · Timetable
                    </h5>
                    <div className="small text-muted mt-1">
                      <strong>Class:</strong> {picked.classGrade}
                      {picked.section && <> <strong className="ms-2">Section:</strong> {picked.section}</>}
                      {academicYear && <> <strong className="ms-2">Academic Year:</strong> {academicYear}</>}
                      <span className="ms-2">· {periodsPerDay} periods/day · {days.length} days/week</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!picked.classGrade ? (
            <div className="card"><div className="card-body text-center py-5 text-muted">
              <i className="bi bi-arrow-up-circle fs-1 d-block mb-2"/>
              Pick a class above to view or build its timetable.
            </div></div>
          ) : loadingGrid ? (
            <div className="text-center py-4"><div className="spinner-border text-primary"/></div>
          ) : (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0 align-middle" style={{ minWidth: 900 }}>
                    <thead style={{ background: '#f1f5f9' }}>
                      <tr>
                        <th style={{ width: 100, position: 'sticky', left: 0, background: '#e2e8f0', zIndex: 2 }}>Period</th>
                        {days.map(d => (
                          <th key={d} className="text-center" style={{ minWidth: 160 }}>{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: periodsPerDay }, (_, i) => {
                        const period = i + 1;
                        const pt = periodTimes.find(p => p.period === period) || {};
                        return (
                          <tr key={period}>
                            <td className="fw-bold text-center" style={{ background: '#f8fafc', position: 'sticky', left: 0, zIndex: 1 }}>
                              <div>P{period}</div>
                              <div className="small text-muted" style={{ fontSize: 10 }}>
                                {pt.startTime || '—'}<br/>{pt.endTime || ''}
                              </div>
                            </td>
                            {days.map(day => {
                              const cell = schedule[cellKey(day, period)];
                              return (
                                <td key={day}
                                  onClick={() => openCell(day, period)}
                                  style={{
                                    cursor: canEdit ? 'pointer' : 'default',
                                    background: cell?.subject ? '#fef3c7' : 'transparent',
                                    minHeight: 70, padding: 6,
                                  }}
                                  title={canEdit ? 'Click to edit' : ''}
                                >
                                  {cell?.subject ? (
                                    <div>
                                      <div className="fw-semibold small">{cell.subject}</div>
                                      {cell.primaryTeacher && (
                                        <div className="small text-primary">
                                          <i className="bi bi-person-fill me-1"/>{cell.primaryTeacher}
                                        </div>
                                      )}
                                      {cell.substituteTeacher && (
                                        <div className="small text-warning">
                                          <i className="bi bi-person-dash me-1"/>Sub: {cell.substituteTeacher}
                                        </div>
                                      )}
                                      {cell.petTeacher && (
                                        <div className="small text-success">
                                          <i className="bi bi-bicycle me-1"/>PET: {cell.petTeacher}
                                        </div>
                                      )}
                                      {cell.room && (
                                        <div className="small text-muted">
                                          <i className="bi bi-door-closed me-1"/>{cell.room}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    canEdit && <div className="text-center text-muted" style={{ fontSize: 11 }}>+ Add</div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="p-2 border-top small text-muted d-flex flex-wrap gap-3">
                  <span><i className="bi bi-person-fill text-primary"/> = Class Teacher</span>
                  <span><i className="bi bi-person-dash text-warning"/> = Substitute</span>
                  <span><i className="bi bi-bicycle text-success"/> = PET / Sports</span>
                  <span><i className="bi bi-door-closed"/> = Room</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─────────────────────────────────────────────── OVERVIEW TAB ──── */}
      {tab === 'overview' && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold"><i className="bi bi-collection me-2 text-primary"/>All Class Timetables</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchOverview}><i className="bi bi-arrow-clockwise"/></button>
          </div>
          <div className="card-body p-0">
            {overviewLoading ? (
              <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
            ) : overview.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-inbox fs-1 d-block mb-2"/>
                No timetables yet. Switch to <strong>Grid</strong>, pick a class, and start filling cells.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Class / Grade</th>
                      <th>Section</th>
                      <th>Academic Year</th>
                      <th style={{ width: 120 }}>Periods / Day</th>
                      <th style={{ width: 130 }}>Slots Filled</th>
                      <th>Last Updated</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.map(g => (
                      <tr key={g._id} role="button" onClick={() => { setPicked({ classGrade: g.classGrade, section: g.section }); setAcademicYear(g.academicYear); setTab('grid'); }}>
                        <td className="fw-semibold">{g.classGrade}</td>
                        <td>{g.section || '—'}</td>
                        <td>{g.academicYear || '—'}</td>
                        <td>{g.periodsPerDay}</td>
                        <td>
                          <span className="text-success fw-semibold">{g.slotsFilled}</span>
                          <span className="text-muted">/{g.totalSlots}</span>
                        </td>
                        <td className="small text-muted">{new Date(g.updatedAt).toLocaleString('en-IN')}</td>
                        <td><i className="bi bi-pencil-square text-primary"/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'policy' && <PolicyTab moduleKey="staff_timetable" label="Staff Timetable — Manual & Policy" />}

      {/* ─── Cell Editor Modal ──────────────────────────────────────── */}
      {editCell && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editCell.day} · Period {editCell.period}
                  <span className="ms-2 small text-muted">
                    ({periodTimes.find(p => p.period === editCell.period)?.startTime || '?'}–{periodTimes.find(p => p.period === editCell.period)?.endTime || '?'})
                  </span>
                </h5>
                <button className="btn-close" onClick={closeCell}/>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Subject</label>
                    {cfg.subjects.length ? (
                      <select className="form-select" value={cellForm.subject} onChange={e => setCellForm(p => ({ ...p, subject: e.target.value }))}>
                        <option value="">— No class —</option>
                        {cfg.subjects.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input className="form-control" value={cellForm.subject} onChange={e => setCellForm(p => ({ ...p, subject: e.target.value }))} />
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Room</label>
                    <input className="form-control" placeholder="Room 101" value={cellForm.room} onChange={e => setCellForm(p => ({ ...p, room: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      <i className="bi bi-person-fill text-primary me-1"/>Class Teacher (Primary)
                    </label>
                    <StaffPicker staff={staff} error={staffError} value={cellForm.primaryTeacher}
                      onChange={v => setCellForm(p => ({ ...p, primaryTeacher: v }))}
                      placeholder="Mr / Ms / Dr — guest staff" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      <i className="bi bi-person-dash text-warning me-1"/>Substitute Teacher
                    </label>
                    <StaffPicker staff={staff} error={staffError} value={cellForm.substituteTeacher}
                      onChange={v => setCellForm(p => ({ ...p, substituteTeacher: v }))}
                      placeholder="Backup teacher (manual entry)" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      <i className="bi bi-bicycle text-success me-1"/>PET / Sports / Co-curricular Teacher
                    </label>
                    <StaffPicker staff={staff} error={staffError} value={cellForm.petTeacher}
                      onChange={v => setCellForm(p => ({ ...p, petTeacher: v }))}
                      placeholder="If this period is sports / drill" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={2} value={cellForm.notes} onChange={e => setCellForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-danger me-auto" onClick={clearCell}>
                  <i className="bi bi-trash me-1"/>Clear Cell
                </button>
                <button className="btn btn-secondary" onClick={closeCell}>Cancel</button>
                <button className="btn btn-primary" onClick={saveCell} disabled={savingCell}>
                  {savingCell && <span className="spinner-border spinner-border-sm me-1"/>}
                  {recordId ? 'Save Cell' : 'Apply (Save Timetable to persist)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── StaffPicker ────────────────────────────────────────────────────────────
// Dropdown of every faculty/teacher in the tenant + an "Other (enter manually)"
// option for guest staff or any one-off case the admin wants to type. The saved
// value is just the display name (string), so it's compatible with timetable
// data already on disk and with any external printout / export.
const OTHER_STAFF = '__other__';
function StaffPicker({ staff, error, value, onChange, placeholder = 'Type a name...' }) {
  // Build a lookup of "First Last" → user, then determine if `value` is one of
  // those names (dropdown match) or something custom (other-mode).
  const names = (staff || []).map(s => `${s.firstName || ''} ${s.lastName || ''}`.trim()).filter(Boolean);
  const inList = !!value && names.includes(value);
  const [otherMode, setOtherMode] = useState(false);
  const showOther = otherMode || (!!value && !inList);

  const onSelect = (e) => {
    const v = e.target.value;
    if (v === OTHER_STAFF) {
      setOtherMode(true);
      if (inList) onChange('');   // start fresh in the input
    } else {
      setOtherMode(false);
      onChange(v);
    }
  };

  return (
    <>
      <select className="form-select" value={showOther ? OTHER_STAFF : (value || '')} onChange={onSelect}>
        <option value="">— None —</option>
        {(staff || []).map(s => {
          const name = `${s.firstName || ''} ${s.lastName || ''}`.trim();
          if (!name) return null;
          return (
            <option key={s._id} value={name}>
              {name}{s.roleKey ? ` · ${s.roleKey.replace(/_/g, ' ')}` : ''}
            </option>
          );
        })}
        <option value={OTHER_STAFF}>Other (enter manually) ↓</option>
      </select>
      {showOther && (
        <input className="form-control mt-1" placeholder={placeholder}
          value={inList ? '' : (value || '')} autoFocus
          onChange={e => onChange(e.target.value)} />
      )}
      {/* Why is the dropdown empty? Surface the actual reason so admins can fix it. */}
      {error && (
        <small className="text-danger d-block mt-1">
          <i className="bi bi-exclamation-triangle me-1"/>
          Couldn't load staff: {error}. Check that your role has <strong>users:view</strong> permission.
        </small>
      )}
      {!error && (!staff || staff.length === 0) && (
        <small className="text-muted d-block mt-1">
          <i className="bi bi-info-circle me-1"/>
          No staff in <strong>Users</strong> module. Open <strong>Users</strong>, add staff records (any role except Student), then re-open this dialog.
        </small>
      )}
      {staff && staff.length > 0 && (
        <small className="text-muted d-block mt-1">
          {staff.length} staff loaded. If someone's missing, check they're <strong>active</strong> in the Users module.
        </small>
      )}
    </>
  );
}
